import { Params } from '@angular/router';
import { Owner } from './evaluation';
import { PageControl } from './page-control';

export enum EvalRescueDateField {
  CREATED_AT = 'CREATED_AT',
  ASSUMED_AT = 'ASSUMED_AT',
  PRICING_ENDED_AT = 'PRICING_ENDED_AT',
  UPDATED_AT = 'UPDATED_AT',
}

export const EVAL_RESCUE_DATE_FIELD_LABELS: Record<EvalRescueDateField, string> = {
  [EvalRescueDateField.CREATED_AT]: 'Data do resgate',
  [EvalRescueDateField.ASSUMED_AT]: 'Data que assumiu o resgate',
  [EvalRescueDateField.PRICING_ENDED_AT]: 'Data de precificação',
  [EvalRescueDateField.UPDATED_AT]: 'Data da última atualização',
};

export enum EvalRescueStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  SENT_TO_STORE = 'SENT_TO_STORE',
  RESCUED = 'RESCUED',
  NOT_RESCUED = 'NOT_RESCUED',
}

export const EVAL_RESCUE_STATUS_LABELS: Record<EvalRescueStatus, string> = {
  [EvalRescueStatus.PENDING]: 'Pendente',
  [EvalRescueStatus.IN_PROGRESS]: 'Em andamento',
  [EvalRescueStatus.SENT_TO_STORE]: 'Enviado para loja',
  [EvalRescueStatus.RESCUED]: 'Resgatado',
  [EvalRescueStatus.NOT_RESCUED]: 'Não resgatado',
};

export const EVAL_RESCUE_STATUS_PILL: Record<EvalRescueStatus, string> = {
  [EvalRescueStatus.PENDING]: 'yellow',
  [EvalRescueStatus.IN_PROGRESS]: 'blue',
  [EvalRescueStatus.SENT_TO_STORE]: 'orange',
  [EvalRescueStatus.RESCUED]: 'green',
  [EvalRescueStatus.NOT_RESCUED]: 'red',
};

export interface EvalRescueReasonOption<T extends string> {
  key: T;
  label: string;
}

export const EVAL_RESCUE_DATE_FIELD_OPTIONS: EvalRescueReasonOption<EvalRescueDateField>[] =
  Object.values(EvalRescueDateField).map((key) => ({
    key,
    label: EVAL_RESCUE_DATE_FIELD_LABELS[key],
  }));

export enum EvalRescueInProgressReason {
  AWAITING_CUSTOMER_RETURN = 'AWAITING_CUSTOMER_RETURN',
  IN_NEGOTIATION = 'IN_NEGOTIATION',
  REEVALUATION_SCHEDULED = 'REEVALUATION_SCHEDULED',
  PROPOSAL_RESENT = 'PROPOSAL_RESENT',
  DOCUMENTATION_UNDER_REVIEW = 'DOCUMENTATION_UNDER_REVIEW',
}

export const EVAL_RESCUE_IN_PROGRESS_REASON_LABELS: Record<EvalRescueInProgressReason, string> = {
  [EvalRescueInProgressReason.AWAITING_CUSTOMER_RETURN]: 'Aguardando retorno do cliente',
  [EvalRescueInProgressReason.IN_NEGOTIATION]: 'Em negociação',
  [EvalRescueInProgressReason.REEVALUATION_SCHEDULED]: 'Reavaliação agendada',
  [EvalRescueInProgressReason.PROPOSAL_RESENT]: 'Proposta reenviada',
  [EvalRescueInProgressReason.DOCUMENTATION_UNDER_REVIEW]: 'Documentação em análise',
};

export enum EvalRescueNotRescuedReason {
  INCORRECT_CONTACT_DATA = 'INCORRECT_CONTACT_DATA',
  CONTACT_NOT_ANSWERED = 'CONTACT_NOT_ANSWERED',
  PRIVATE_SALE = 'PRIVATE_SALE',
  CLOSED_AT_ANOTHER_STORE = 'CLOSED_AT_ANOTHER_STORE',
  NEGOTIATED_BY_EVAL_DEALER = 'NEGOTIATED_BY_EVAL_DEALER',
  SELLER_PHONE = 'SELLER_PHONE',
  CUSTOMER_NOT_AVAILABLE_FOR_NEGOTIATION = 'CUSTOMER_NOT_AVAILABLE_FOR_NEGOTIATION',
  NEGOTIATION_NOT_CONCLUDED_BY_EVAL_DEALER = 'NEGOTIATION_NOT_CONCLUDED_BY_EVAL_DEALER',
  UNREALISTIC_EXPECTATION = 'UNREALISTIC_EXPECTATION'
}

export const EVAL_RESCUE_NOT_RESCUED_REASON_LABELS: Record<EvalRescueNotRescuedReason, string> = {
  [EvalRescueNotRescuedReason.INCORRECT_CONTACT_DATA]: 'Dados de contato incorretos',
  [EvalRescueNotRescuedReason.CONTACT_NOT_ANSWERED]: 'Sem retorno via mensagem e ligação',
  [EvalRescueNotRescuedReason.PRIVATE_SALE]: 'Venda particular',
  [EvalRescueNotRescuedReason.CLOSED_AT_ANOTHER_STORE]: 'Cliente fechou em outra loja do grupo',
  [EvalRescueNotRescuedReason.NEGOTIATED_BY_EVAL_DEALER]: 'Negociado pela loja',
  [EvalRescueNotRescuedReason.SELLER_PHONE]: 'Telefone do vendedor',
  [EvalRescueNotRescuedReason.CUSTOMER_NOT_AVAILABLE_FOR_NEGOTIATION]: 'Cliente não está no momento de negociação',
  [EvalRescueNotRescuedReason.NEGOTIATION_NOT_CONCLUDED_BY_EVAL_DEALER]: 'Negociação não concluída pela loja',
  [EvalRescueNotRescuedReason.UNREALISTIC_EXPECTATION]: 'Expectativa fora de mercado',
};

export const EVAL_RESCUE_IN_PROGRESS_REASON_OPTIONS: EvalRescueReasonOption<EvalRescueInProgressReason>[] =
  Object.values(EvalRescueInProgressReason).map((key) => ({
    key,
    label: EVAL_RESCUE_IN_PROGRESS_REASON_LABELS[key],
  }));

export const EVAL_RESCUE_NOT_RESCUED_REASON_OPTIONS: EvalRescueReasonOption<EvalRescueNotRescuedReason>[] =
  Object.values(EvalRescueNotRescuedReason)
    .map((key) => ({
      key,
      label: EVAL_RESCUE_NOT_RESCUED_REASON_LABELS[key],
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

export const EVAL_RESCUE_TERMINAL_STATUSES = [
  EvalRescueStatus.RESCUED,
  EvalRescueStatus.NOT_RESCUED,
] as const;

// Sentinel for filter "Sem responsável"
export const EVAL_RESCUE_NO_OWNER = '__NO_OWNER__';

export interface EvalRescueComment {
  comment?: string | null;
  createdAt?: string | number | null;
  userId?: string | null;
  username?: string | null;
  edited?: boolean | null;
  editedAt?: string | number | null;
}

export interface EvalRescue {
  id: string;
  evalVehicleId: string;
  status: EvalRescueStatus;
  makeName?: string | null;
  modelName?: string | null;
  trimName?: string | null;
  segment?: string | null;
  manufactureYear?: number | null;
  modelYear?: number | null;
  numberPlate?: string | null;
  km?: number | null;
  color?: string | null;
  fuel?: string | null;
  transmission?: string | null;
  doors?: number | null;
  type?: string | null;
  evalReason?: string | null;
  fipePrice?: number | null;
  pricedValue?: number | null;
  fipePercentage?: number | null;
  pricingEndedAt?: string | number | null;
  createdAt?: string | number | null;
  updatedAt?: string | number | null;
  oemDealerId?: string | null;
  oemDealerName?: string | null;
  evalDealerId?: string | null;
  evalDealerName?: string | null;
  city?: string | null;
  uf?: string | null;
  observationsCount?: number | null;
  contactHidden?: boolean | null;
  availableToAssume?: boolean | null;
  assigneeUserId?: string | null;
  assigneeUserName?: string | null;
  assumedAt?: string | number | null;
  inProgressReason?: EvalRescueInProgressReason | string | null;
  notRescuedReason?: EvalRescueNotRescuedReason | string | null;
  owner?: Partial<Owner> | null;
  comments?: EvalRescueComment[] | null;
  previousRescuesCount?: number | null;
}

export interface EvalRescueHistoryItem {
  date: string;
  assigneeUserName?: string | null;
  status: EvalRescueStatus;
  inProgressReason?: EvalRescueInProgressReason | string | null;
  notRescuedReason?: EvalRescueNotRescuedReason | string | null;
}

export interface EvalRescueHistory {
  rescueId: string;
  numberPlate?: string | null;
  ownerName?: string | null;
  items: EvalRescueHistoryItem[];
}

export interface EvalRescueAssignableUser {
  id: string;
  name: string;
  email?: string | null;
}

export interface EvalRescueFilterOption {
  id: string;
  name: string;
}

export interface EvalRescueFilterOptions {
  units: EvalRescueFilterOption[];
  owners: EvalRescueFilterOption[];
  ufs: string[];
}

export interface EvalRescueStageBreakdown {
  key: string;
  label: string;
  count: number;
}

export interface EvalRescueSummary {
  total: number;
  pending: number;
  inProgress: number;
  sentToStore: number;
  rescued: number;
  notRescued: number;
  inProgressReasons?: Partial<Record<EvalRescueInProgressReason, number>> | null;
  notRescuedReasons?: Partial<Record<EvalRescueNotRescuedReason, number>> | null;
}

export function toInProgressReasonBreakdown(
  reasons: Partial<Record<EvalRescueInProgressReason, number>> | null | undefined,
): EvalRescueStageBreakdown[] {
  if (!reasons) {
    return [];
  }
  return Object.entries(reasons)
    .filter((entry): entry is [EvalRescueInProgressReason, number] => {
      const [, count] = entry;
      return typeof count === 'number' && count > 0 && entry[0] in EVAL_RESCUE_IN_PROGRESS_REASON_LABELS;
    })
    .map(([key, count]) => ({
      key,
      label: EVAL_RESCUE_IN_PROGRESS_REASON_LABELS[key],
      count,
    }));
}

export function toNotRescuedReasonBreakdown(
  reasons: Partial<Record<EvalRescueNotRescuedReason, number>> | null | undefined,
): EvalRescueStageBreakdown[] {
  if (!reasons) {
    return [];
  }
  return Object.entries(reasons)
    .filter((entry): entry is [EvalRescueNotRescuedReason, number] => {
      const [, count] = entry;
      return typeof count === 'number' && count > 0 && entry[0] in EVAL_RESCUE_NOT_RESCUED_REASON_LABELS;
    })
    .map(([key, count]) => ({
      key,
      label: EVAL_RESCUE_NOT_RESCUED_REASON_LABELS[key],
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'pt-BR'));
}

export interface EvalRescueStatusPayload {
  status: EvalRescueStatus;
  inProgressReason?: EvalRescueInProgressReason;
  notRescuedReason?: EvalRescueNotRescuedReason;
}

export interface EvalRescueAssignPayload {
  targetUserId: string;
}

export type EvalRescueFilterPatch = Partial<
  Pick<
    EvalRescueFilter,
    | 'searchQuery'
    | 'ownerPhoneNumber'
    | 'statuses'
    | 'inProgressReasons'
    | 'notRescuedReasons'
    | 'evalDealerId'
    | 'assigneeUserId'
    | 'ufs'
    | 'cityIds'
    | 'dateField'
    | 'periodIni'
    | 'periodEnd'
  >
> & { pageControl?: Partial<PageControl> };

export class EvalRescueFilter {
  pageControl: PageControl;
  searchQuery: string;
  ownerPhoneNumber: string;
  statuses: EvalRescueStatus[];
  inProgressReasons: EvalRescueInProgressReason[];
  notRescuedReasons: EvalRescueNotRescuedReason[];
  evalDealerId: string | null;
  assigneeUserId: string | null;
  ufs: string[];
  cityIds: string[];
  dateField: EvalRescueDateField;
  periodIni: string | null;
  periodEnd: string | null;

  constructor() {
    this.pageControl = {
      page: 0,
      pageSize: 10,
      param: '',
      sortDirection: 'DESC',
      sortField: 'createdAt',
      value: {},
    };
    this.searchQuery = '';
    this.ownerPhoneNumber = '';
    this.statuses = [];
    this.inProgressReasons = [];
    this.notRescuedReasons = [];
    this.evalDealerId = null;
    this.assigneeUserId = null;
    this.ufs = [];
    this.cityIds = [];
    this.dateField = EvalRescueDateField.UPDATED_AT;
    this.periodIni = null;
    this.periodEnd = null;
  }

  static fromParams(params: Params): EvalRescueFilter {
    const filter = new EvalRescueFilter();
    filter.updateFromParamMap(params);
    return filter;
  }

  // Toda alteração gera uma nova instância: os componentes de filtro nunca mutam o objeto recebido
  with(patch: EvalRescueFilterPatch): EvalRescueFilter {
    const { pageControl, ...fields } = patch;
    const next = this.clone();
    Object.assign(next, fields);
    if (pageControl) {
      next.pageControl = { ...next.pageControl, ...pageControl };
    }
    return next;
  }

  clone(): EvalRescueFilter {
    const copy = new EvalRescueFilter();
    copy.pageControl = { ...this.pageControl };
    copy.searchQuery = this.searchQuery;
    copy.ownerPhoneNumber = this.ownerPhoneNumber;
    copy.statuses = [...this.statuses];
    copy.inProgressReasons = [...this.inProgressReasons];
    copy.notRescuedReasons = [...this.notRescuedReasons];
    copy.evalDealerId = this.evalDealerId;
    copy.assigneeUserId = this.assigneeUserId;
    copy.ufs = [...this.ufs];
    copy.cityIds = [...this.cityIds];
    copy.dateField = this.dateField;
    copy.periodIni = this.periodIni;
    copy.periodEnd = this.periodEnd;
    return copy;
  }

  toParamMap(): Params {
    const params: Params = {
      page: this.pageControl.page,
      pageSize: this.pageControl.pageSize,
      sortField: this.pageControl.sortField,
      sortDirection: this.pageControl.sortDirection,
    };

    if (this.searchQuery) {
      params.searchQuery = this.searchQuery;
    }
    if (this.ownerPhoneNumber) {
      params.ownerPhoneNumber = this.ownerPhoneNumber;
    }
    if (this.statuses?.length) {
      params.statuses = this.statuses.join(',');
    }
    if (this.inProgressReasons?.length) {
      params.inProgressReasons = this.inProgressReasons.join(',');
    }
    if (this.notRescuedReasons?.length) {
      params.notRescuedReasons = this.notRescuedReasons.join(',');
    }
    if (this.evalDealerId) {
      params.evalDealerId = this.evalDealerId;
    }
    if (this.assigneeUserId) {
      params.assigneeUserId = this.assigneeUserId;
    }
    if (this.ufs?.length) {
      params.ufs = this.ufs.join(',');
    }
    if (this.cityIds?.length) {
      params.cityIds = this.cityIds.join(',');
    }
    if (this.dateField !== EvalRescueDateField.UPDATED_AT || this.periodIni || this.periodEnd) {
      params.dateField = this.dateField;
    }
    if (this.periodIni) {
      params.periodIni = this.periodIni;
    }
    if (this.periodEnd) {
      params.periodEnd = this.periodEnd;
    }

    return params;
  }

  updateFromParamMap(params: Params): void {
    if (params.page !== undefined) {
      this.pageControl.page = Number(params.page) || 0;
    }
    if (params.pageSize !== undefined) {
      this.pageControl.pageSize = Number(params.pageSize) || 10;
    }
    if (params.sortField) {
      this.pageControl.sortField = String(params.sortField);
    }
    if (params.sortDirection === 'ASC' || params.sortDirection === 'DESC') {
      this.pageControl.sortDirection = params.sortDirection;
    }
    if (params.searchQuery !== undefined) {
      this.searchQuery = String(params.searchQuery);
    }
    if (params.ownerPhoneNumber !== undefined) {
      this.ownerPhoneNumber = String(params.ownerPhoneNumber);
    }
    if (params.statuses) {
      this.statuses = String(params.statuses)
        .split(',')
        .filter(Boolean) as EvalRescueStatus[];
    } else if (params.status) {
      this.statuses = [params.status as EvalRescueStatus];
    }
    if (params.inProgressReasons) {
      this.inProgressReasons = String(params.inProgressReasons)
        .split(',')
        .filter(Boolean) as EvalRescueInProgressReason[];
    } else if (params.currentStage && params.currentStage in EVAL_RESCUE_IN_PROGRESS_REASON_LABELS) {
      this.inProgressReasons = [params.currentStage as EvalRescueInProgressReason];
    }
    if (params.notRescuedReasons) {
      this.notRescuedReasons = String(params.notRescuedReasons)
        .split(',')
        .filter(Boolean) as EvalRescueNotRescuedReason[];
    } else if (params.currentStage && params.currentStage in EVAL_RESCUE_NOT_RESCUED_REASON_LABELS) {
      this.notRescuedReasons = [params.currentStage as EvalRescueNotRescuedReason];
    }
    if (params.evalDealerId) {
      this.evalDealerId = String(params.evalDealerId);
    } else if (params.unitId) {
      this.evalDealerId = String(params.unitId);
    }
    if (params.assigneeUserId) {
      this.assigneeUserId = String(params.assigneeUserId);
    } else if (params.ownerId) {
      this.assigneeUserId = String(params.ownerId);
    }
    if (params.ufs) {
      this.ufs = String(params.ufs).split(',').filter(Boolean);
    } else if (params.states) {
      this.ufs = String(params.states).split(',').filter(Boolean);
    } else if (params.state) {
      this.ufs = [String(params.state)];
    }
    if (params.cityIds) {
      this.cityIds = String(params.cityIds).split(',').filter(Boolean);
    } else if (params.cityId) {
      this.cityIds = [String(params.cityId)];
    }
    if (params.dateField && params.dateField in EVAL_RESCUE_DATE_FIELD_LABELS) {
      this.dateField = params.dateField as EvalRescueDateField;
    }
    if (params.periodIni) {
      this.periodIni = String(params.periodIni);
    }
    if (params.periodEnd) {
      this.periodEnd = String(params.periodEnd);
    }
    if (!params.periodIni && !params.periodEnd && (params.createdAtIni || params.createdAtEnd)) {
      this.periodIni = params.createdAtIni ? String(params.createdAtIni) : null;
      this.periodEnd = params.createdAtEnd ? String(params.createdAtEnd) : null;
      if (!params.dateField) {
        this.dateField = EvalRescueDateField.CREATED_AT;
      }
    }
  }

  matches(rescue: EvalRescue): boolean {
    if (this.statuses?.length && !this.statuses.includes(rescue.status)) {
      return false;
    }
    if (
      this.inProgressReasons?.length &&
      !this.inProgressReasons.includes(rescue.inProgressReason as EvalRescueInProgressReason)
    ) {
      return false;
    }
    if (
      this.notRescuedReasons?.length &&
      !this.notRescuedReasons.includes(rescue.notRescuedReason as EvalRescueNotRescuedReason)
    ) {
      return false;
    }
    if (this.evalDealerId && rescue.evalDealerId !== this.evalDealerId) {
      return false;
    }
    if (this.assigneeUserId === EVAL_RESCUE_NO_OWNER) {
      if (rescue.assigneeUserId) {
        return false;
      }
    } else if (this.assigneeUserId && rescue.assigneeUserId !== this.assigneeUserId) {
      return false;
    }
    if (this.ufs?.length && (!rescue.uf || !this.ufs.includes(rescue.uf))) {
      return false;
    }
    if (this.searchQuery?.trim()) {
      const query = this.searchQuery.trim().toLowerCase();
      const haystack = [rescue.numberPlate, rescue.makeName, rescue.modelName, rescue.trimName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(query)) {
        return false;
      }
    }

    const periodValue = toComparableTime(rescueDateByField(rescue, this.dateField));
    if (this.periodIni) {
      const start = toComparableTime(this.periodIni);
      if (periodValue != null && start != null && periodValue < start) {
        return false;
      }
    }
    if (this.periodEnd) {
      const end = toEndOfDayTime(this.periodEnd);
      if (periodValue != null && end != null && periodValue > end) {
        return false;
      }
    }

    return true;
  }
}

function rescueDateByField(
  rescue: EvalRescue,
  dateField: EvalRescueDateField,
): string | number | null | undefined {
  switch (dateField) {
    case EvalRescueDateField.ASSUMED_AT:
      return rescue.assumedAt;
    case EvalRescueDateField.PRICING_ENDED_AT:
      return rescue.pricingEndedAt;
    case EvalRescueDateField.UPDATED_AT:
      return rescue.updatedAt;
    case EvalRescueDateField.CREATED_AT:
    default:
      return rescue.createdAt;
  }
}

function toComparableTime(value: string | number | null | undefined): number | null {
  if (value == null || value === '') {
    return null;
  }
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

function toEndOfDayTime(value: string): number | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const time = new Date(`${value}T23:59:59.999`).getTime();
    return Number.isFinite(time) ? time : null;
  }
  return toComparableTime(value);
}

export function evalRescueReasonLabel(rescue: EvalRescue): string | null {
  const inProgress = rescue.inProgressReason as EvalRescueInProgressReason | null | undefined;
  if (inProgress && inProgress in EVAL_RESCUE_IN_PROGRESS_REASON_LABELS) {
    return EVAL_RESCUE_IN_PROGRESS_REASON_LABELS[inProgress];
  }
  const notRescued = rescue.notRescuedReason as EvalRescueNotRescuedReason | null | undefined;
  if (notRescued && notRescued in EVAL_RESCUE_NOT_RESCUED_REASON_LABELS) {
    return EVAL_RESCUE_NOT_RESCUED_REASON_LABELS[notRescued];
  }
  return null;
}

export interface EvalVehicleRescueDetails {
  status: EvalRescueStatus;
  assumedAt?: string | number | Date | null;
  substatus?: string | null;
}

export function evalVehicleRescueSubstatusLabel(
  rescue: Pick<EvalVehicleRescueDetails, 'status' | 'substatus'>,
): string | null {
  const raw = rescue.substatus?.trim();
  if (raw) {
    if (raw in EVAL_RESCUE_IN_PROGRESS_REASON_LABELS) {
      return EVAL_RESCUE_IN_PROGRESS_REASON_LABELS[raw as EvalRescueInProgressReason];
    }
    if (raw in EVAL_RESCUE_NOT_RESCUED_REASON_LABELS) {
      return EVAL_RESCUE_NOT_RESCUED_REASON_LABELS[raw as EvalRescueNotRescuedReason];
    }
    return raw;
  }

  switch (rescue.status) {
    case EvalRescueStatus.PENDING:
      return 'Aguardando responsável';
    case EvalRescueStatus.RESCUED:
      return 'Negócio fechado pelo time de resgate';
    default:
      return null;
  }
}